/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * "Describe your automation": a description in, a draft automation out.
 *
 * Posts to POST /automations/ai/draft and hands the draft (AutomationCreate
 * shape) to `onDraft`, which loads it into the existing wizard. Nothing is
 * saved here; the user reviews and saves in the wizard. Any warnings (a
 * webhook left out, an unknown field dropped) are shown before the hand-off
 * so the user knows what to check, and the draft arrives switched off.
 *
 * The caller decides whether to render this (useAiAssistantStatus).
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";

const MAX_CHARS = 2000;

const EXAMPLES = [
  "When a new lead comes from the website, create a call task for the owner due tomorrow.",
  "When a deal moves to Negotiation and is worth more than 10000, create a high priority review task.",
  "When a case is updated to High priority, email the contact that we are on it.",
];

export default function DescribeAutomationDialog({ open, onClose, onDraft }) {
  const { axiosInstance } = useAuth();
  const { refresh } = useAiAssistantStatus();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setError("");
      setResult(null);
    }
  }, [open]);

  const generate = async () => {
    const text = prompt.trim();
    if (text.length < 3) {
      setError("Describe what should happen first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axiosInstance.post("/automations/ai/draft", { prompt: text });
      const warnings = res.data?.warnings || [];
      if (warnings.length) {
        setResult(res.data);
      } else {
        onDraft(res.data.draft);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "The automation could not be drafted. Please try again."));
      // Out of credits or a seat change: let every AI entry point re-check.
      if ([402, 403].includes(err?.response?.status)) refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesome sx={{ color: colors.brand.primary }} fontSize="small" />
        Describe your automation
      </DialogTitle>
      <DialogContent>
        {result ? (
          <MDBox>
            <MDTypography variant="body2" sx={{ mb: 1.5 }}>
              The draft <strong>{result.draft?.name}</strong> is ready. Check these points in the wizard:
            </MDTypography>
            <Alert severity="warning" sx={{ mb: 1 }}>
              <MDBox component="ul" sx={{ m: 0, pl: 2 }}>
                {result.warnings.map((w) => (
                  <li key={w}>
                    <MDTypography variant="caption" component="span">
                      {w}
                    </MDTypography>
                  </li>
                ))}
              </MDBox>
            </Alert>
          </MDBox>
        ) : (
          <>
            <MDTypography variant="body2" color="text" sx={{ mb: 1.5 }}>
              Say which record it is for, what starts it, any conditions, and what should happen. The
              draft opens in the wizard for you to review; nothing is saved until you save it there.
            </MDTypography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              value={prompt}
              disabled={loading}
              onChange={(e) => setPrompt(e.target.value.slice(0, MAX_CHARS))}
              placeholder={EXAMPLES[0]}
              helperText={`${prompt.length}/${MAX_CHARS}`}
              inputProps={{ "aria-label": "Describe your automation" }}
            />
            <MDBox sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                For example
              </MDTypography>
              {EXAMPLES.map((example) => (
                <MDTypography
                  key={example}
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setPrompt(example)}
                  disabled={loading}
                  sx={{
                    textAlign: "left",
                    border: 0,
                    background: "none",
                    p: 0,
                    cursor: "pointer",
                    color: colors.brand.primary,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {example}
                </MDTypography>
              ))}
            </MDBox>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        {result ? (
          <MDButton variant="gradient" color="info" onClick={() => onDraft(result.draft)}>
            Open in wizard
          </MDButton>
        ) : (
          <MDButton
            variant="gradient"
            color="info"
            onClick={generate}
            disabled={loading || prompt.trim().length < 3}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesome />}
          >
            {loading ? "Drafting..." : "Draft automation"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

DescribeAutomationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDraft: PropTypes.func.isRequired,
};
